import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { trpc } from '@/lib/trpc';
import { Loader2, PieChart as PieChartIcon } from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#f59e0b',
  CONFIRMED: '#3b82f6',
  PARTIALLY_PAID: '#f97316',
  FULLY_PAID: '#10b981',
  TICKETED: '#8b5cf6',
  IN_PROGRESS: '#06b6d4',
  COMPLETED: '#64748b',
  CANCELLED: '#ef4444',
  REFUNDED: '#ec4899',
  PARTIALLY_REFUNDED: '#f9a8d4',
};

export function BookingStatusChart() {
  const { t } = useTranslation('dashboard');
  const { data, isLoading, error } = trpc.dashboard.bookingsByStatus.useQuery();

  if (isLoading) {
    return (
      <div className="luxury-card h-[350px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="luxury-card h-[350px] flex flex-col items-center justify-center">
        <PieChartIcon className="h-12 w-12 text-[hsl(var(--muted-foreground))] mb-3" />
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('noBookingData')}</p>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.count, 0);

  const chartData = data.map((item) => ({
    name: t(`status.${item.status.toLowerCase()}`) || item.status,
    value: item.count,
    status: item.status,
  }));

  return (
    <div className="luxury-card p-0 overflow-hidden">
      <div className="border-b border-[hsl(var(--border))] px-6 py-4">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <PieChartIcon className="h-5 w-5 text-violet-500" />
          {t('bookingDistribution')}
        </h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          {total} {t('totalBookingsLabel')}
        </p>
      </div>
      <div className="p-6">
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={STATUS_COLORS[entry.status] || '#94a3b8'}
                  stroke="hsl(var(--card))"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
              formatter={(value: number, name: string) => [
                `${value} (${((value / total) * 100).toFixed(1)}%)`,
                name,
              ]}
            />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ paddingTop: '20px' }}
              formatter={(value) => (
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
